import {
  GetCloudName,
  GetProviderName,
  GPUInstanceType,
} from "types/InstanceTypes";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import {
  getFilteredBasePriceAmount,
  isFreeTierInstance,
  useFreeTier,
  withServiceCharge,
} from "utils/freeTierUtils";
import ChipToggleButtons from "../../ComputePicker/ChipToggleButtons";
import {
  convertToGPUMap,
  formatMemoryForDisplay,
  parseMemoryToGiB,
} from "../../utils/index";
import ComputeLoader from "../ComputeLoader";
import posthog from "posthog-js";

import NvidiaDGXDataCenter from "assets/img/png/dgxDatacenter.png";
import { useWindupString } from "windups";
import { Button } from "components/UI-lib";
import {
  displayPlatformType,
  dummyDGXInstanceObject,
} from "components/Environment/utils";
import DGXComputeCard from "./DGX/DGXComputeCard";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { useHistory, useLocation } from "react-router";
import { useIdentityFeatureFlagEnabled } from "utils/posthogFeatureFlags";
import RackCard from "../RackCard/index";
import CpuInstanceCard from "../CpuInstanceCard";
import { getPrimaryInstanceCloudCred } from "utils/cloudCreds";
import {
  FunnelIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/outline";
import SortByDropdown from "./SortByDropdown";
import { gpuNamesMatch } from "utils/gpuNames";

export interface AdvancedSelectorProps {
  allGPUInstances: GPUInstanceType[] | undefined;
  displayGPUInstances: GPUInstanceType[] | undefined;
  cpuInstances: GPUInstanceType[];
  setSelectedInstance(instanceObj: GPUInstanceType | undefined): void;
  containerSelected: ContainerSelected;
  selectedCloudName: string;
  isLoadingInstances: boolean;
  hideDgxc: boolean;
  persistentOnly: boolean;
  flexiblePortControlOnly: boolean;
  flexibleStorageOnly: boolean;
  animateLoaderCarouselLayout?: boolean;
  syncGpuSelectionToUrl?: boolean;
  initialSelectedChip?: string;
  pickerAllowedLocations?: Set<string> | null;
}
import queryString from "query-string";

const AdvancedSelector: React.FC<AdvancedSelectorProps> = ({
  allGPUInstances,
  displayGPUInstances,
  cpuInstances,
  setSelectedInstance,
  containerSelected,
  selectedCloudName,
  isLoadingInstances,
  hideDgxc,
  persistentOnly,
  flexiblePortControlOnly,
  flexibleStorageOnly,
  animateLoaderCarouselLayout,
  syncGpuSelectionToUrl = true,
  initialSelectedChip,
  pickerAllowedLocations,
}) => {
  const location = useLocation();
  const history = useHistory();
  const [localSelectedGpu, setLocalSelectedGpu] = useState<string | undefined>(
    initialSelectedChip,
  );
  const dgxcOnly = useIdentityFeatureFlagEnabled("dgxc-only");
  const queryParams = queryString.parse(location.search);

  // CPU instances for the current cloud: when GPU list has items, only show CPUs from same provider(s); when no GPUs (e.g. GPU-specific filters), show all CPUs
  const displayCPUInstances = useMemo(() => {
    if (displayGPUInstances === undefined) return [];
    return cpuInstances;
  }, [cpuInstances, displayGPUInstances]);

  const gpuList = useMemo(() => {
    if (displayGPUInstances === undefined) return undefined;
    const gpuInstances = dgxcOnly
      ? displayGPUInstances.filter(
          (instance) =>
            getPrimaryInstanceCloudCred(instance)?.id === "dgxc-gcp-1",
        )
      : displayGPUInstances;
    const _gpuMap = convertToGPUMap(gpuInstances);
    const gpuChips = Object.keys(_gpuMap).map((gpuName) => {
      return {
        name: gpuName,
        manufacturer: _gpuMap[gpuName][0].supported_gpus[0].manufacturer,
      };
    });
    // CPU first, then GPU chips (so divider after index 0 when CPU is present)
    if (displayCPUInstances.length > 0) {
      return [{ name: "CPU", manufacturer: "" }, ...gpuChips];
    }
    return gpuChips;
  }, [dgxcOnly, displayGPUInstances, displayCPUInstances]);

  const setSelectedGpu = useCallback(
    (gpu: string) => {
      if (!syncGpuSelectionToUrl) {
        setLocalSelectedGpu(gpu);
        return;
      }

      // Parse query params fresh to avoid stale closure
      const currentParams = queryString.parse(location.search);
      const newUrl = `?${queryString.stringify({ ...currentParams, gpu })}`;
      history.replace(newUrl);
    },
    [history, location.search, syncGpuSelectionToUrl],
  );

  const selectedGpu: string | undefined = useMemo(() => {
    const gpuQueryParam = syncGpuSelectionToUrl
      ? (queryParams.gpu ?? queryParams.chip ?? "")
      : (localSelectedGpu ?? "");
    const _gpu = (
      typeof gpuQueryParam === "string"
        ? gpuQueryParam
        : (gpuQueryParam?.[0] ?? "")
    )?.trim();

    // Special handling for DGX instances
    if (_gpu.toLowerCase() === "dgx-h100") {
      return "dgx-H100";
    }

    if (_gpu.toLowerCase() === "cpu") {
      return "CPU";
    }

    const gpuFromQueryParam =
      _gpu && _gpu.length > 0
        ? gpuList?.find((g) => gpuNamesMatch(g.name, _gpu))
        : undefined;

    if (gpuFromQueryParam) {
      return gpuFromQueryParam.name;
    }
    const defaultGpuName = dgxcOnly ? "dgx-H100" : "L40S";
    const defaultGpu = gpuList?.find(
      (g) => g.name.toLowerCase() === defaultGpuName.toLowerCase(),
    );
    return defaultGpu?.name ?? gpuList?.[0]?.name;
  }, [
    queryParams.chip,
    queryParams.gpu,
    dgxcOnly,
    gpuList,
    localSelectedGpu,
    syncGpuSelectionToUrl,
  ]);

  if (isLoadingInstances) {
    return (
      <div className="py-20">
        <ComputeLoader animateCarouselLayout={animateLoaderCarouselLayout} />
      </div>
    );
  }

  if (displayGPUInstances === undefined) {
    return null; // Instances aren't loading but we haven't filtered the instances yet
  }

  // Only show empty state when neither GPU nor CPU options exist for current filters/cloud
  if (displayGPUInstances.length === 0 && displayCPUInstances.length === 0) {
    return (
      <EmptyState
        selectedGpuCount="All"
        selectedVram="All"
        onResetFilters={() => {}}
      />
    );
  }

  return (
    <>
      <div className="flex">
        <ChipToggleButtons
          allGPUInstances={allGPUInstances ?? []}
          selectedChip={selectedGpu ?? null}
          setSelectedChip={(gpuName) => {
            setSelectedGpu(gpuName);
          }}
          selectedCloud={selectedCloudName}
          listOfChips={gpuList ?? []}
          loading={isLoadingInstances}
          hideDgxc={hideDgxc}
          dividerAfterIndex={displayCPUInstances.length > 0 ? 0 : undefined}
        />
      </div>
      <hr className="w-full my-5 dark:border dark:border-zinc-800" />
      <div className="flex flex-col">
        <div className="flex flex-col gap-3">
          {selectedGpu !== undefined &&
            selectedGpu !== "dgx-H100" &&
            selectedGpu !== "CPU" && (
              <ComputeOptions
                selectedGpu={selectedGpu}
                flexiblePortControlOnly={flexiblePortControlOnly}
                flexibleStorageOnly={flexibleStorageOnly}
                pickerAllowedLocations={pickerAllowedLocations}
                displayGPUInstances={displayGPUInstances
                  .filter(
                    (instance) =>
                      instance.supported_gpus &&
                      instance.supported_gpus.length > 0 &&
                      gpuNamesMatch(
                        instance.supported_gpus[0].name,
                        selectedGpu,
                      ),
                  )
                  .filter((instance) =>
                    !!containerSelected.vmBuild ||
                    !!containerSelected.customContainer
                      ? getPrimaryInstanceCloudCred(instance)?.platformType !==
                        "akash"
                      : true,
                  )
                  .sort((a, b) => {
                    return (
                      Number(
                        getFilteredBasePriceAmount(a, pickerAllowedLocations),
                      ) -
                      Number(
                        getFilteredBasePriceAmount(b, pickerAllowedLocations),
                      )
                    );
                  })}
                setSelectedInstance={(instance) => {
                  setSelectedInstance(instance);
                  posthog.capture("Selected Instance", {
                    tab: "Advanced",
                    instanceType: instance?.type,
                    gpuName: instance?.supported_gpus
                      ? instance?.supported_gpus[0].name
                      : "",
                    cloudProvider:
                      getPrimaryInstanceCloudCred(instance)?.platformType,
                  });
                }}
              />
            )}
          {selectedGpu === "dgx-H100" && (
            <DGXSetup setSelectedInstance={setSelectedInstance} />
          )}
          {selectedGpu === "CPU" && (
            <CPUOptions
              cpuInstances={displayCPUInstances}
              flexiblePortControlOnly={flexiblePortControlOnly}
              flexibleStorageOnly={flexibleStorageOnly}
              pickerAllowedLocations={pickerAllowedLocations}
              setSelectedInstance={(instance) => {
                setSelectedInstance(instance);
                posthog.capture("Selected Instance", {
                  tab: "Advanced",
                  instanceType: instance?.type,
                  gpuName: "",
                  cloudProvider:
                    getPrimaryInstanceCloudCred(instance)?.platformType,
                });
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AdvancedSelector;

interface ComputeOptionsProps {
  selectedGpu: string;
  displayGPUInstances: GPUInstanceType[];
  setSelectedInstance(instanceObj: GPUInstanceType | undefined): void;
  flexiblePortControlOnly: boolean;
  flexibleStorageOnly: boolean;
  pickerAllowedLocations?: Set<string> | null;
}

const ComputeOptions: React.FC<ComputeOptionsProps> = ({
  displayGPUInstances,
  setSelectedInstance,
  selectedGpu,
  flexiblePortControlOnly,
  flexibleStorageOnly,
  pickerAllowedLocations,
}) => {
  // Sorting state
  const [sortBy, setSortBy] = useState<string>("price");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { isEntitled } = useFreeTier();

  // Check if there are multiple VRAM options
  const hasMultipleVramOptions = useMemo(() => {
    const vramSet = new Set<string>();
    displayGPUInstances.forEach((instance) => {
      if (instance.supported_gpus && instance.supported_gpus.length > 0) {
        vramSet.add(instance.supported_gpus[0].memory);
      }
    });
    return vramSet.size > 1;
  }, [displayGPUInstances]);

  // Sort instances
  const sortedInstances = useMemo(() => {
    const instances = [...displayGPUInstances];

    instances.sort((a, b) => {
      let aValue: number = 0;
      let bValue: number = 0;

      switch (sortBy) {
        case "price":
          aValue = Number(
            getFilteredBasePriceAmount(a, pickerAllowedLocations) || 0,
          );
          bValue = Number(
            getFilteredBasePriceAmount(b, pickerAllowedLocations) || 0,
          );
          break;
        case "vram":
          aValue = parseMemoryToGiB(a.supported_gpus?.[0]?.memory || "0");
          bValue = parseMemoryToGiB(b.supported_gpus?.[0]?.memory || "0");
          break;
        case "ram":
          aValue = parseMemoryToGiB(a.memory);
          bValue = parseMemoryToGiB(b.memory);
          break;
        case "cpu":
          aValue = Number(a.vcpu || 0);
          bValue = Number(b.vcpu || 0);
          break;
      }

      // Handle sort direction
      if (sortDirection === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return instances;
  }, [displayGPUInstances, sortBy, sortDirection, pickerAllowedLocations]);

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between">
        {selectedGpu && (
          <>
            <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white my-3">
              Select a{" "}
              <span className="text-highlight">NVIDIA {selectedGpu}</span>{" "}
              instance type
            </h2>

            {/* Sort By Dropdown */}
            <div className="flex flex-row gap-3 items-center mb-4">
              <SortByDropdown
                sortBy={sortBy}
                setSortBy={setSortBy}
                hasMultipleVramOptions={hasMultipleVramOptions}
              />
              <button
                onClick={() =>
                  setSortDirection(sortDirection === "asc" ? "desc" : "asc")
                }
                className="flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
                title={sortDirection === "asc" ? "Ascending" : "Descending"}
              >
                {sortDirection === "asc" ? (
                  <ArrowUpIcon className="w-4 h-4" />
                ) : (
                  <ArrowDownIcon className="w-4 h-4" />
                )}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Rack Cards Grid */}

      <div className="flex flex-col gap-3">
        {sortedInstances
          .filter(
            (instance) =>
              instance.supported_gpus && instance.supported_gpus.length > 0,
          )
          .map((instance) => (
            <RackCard
              key={instance.type}
              instanceType={instance}
              setInstanceType={setSelectedInstance}
              gpuCount={instance.supported_gpus[0].count}
              vramSize={instance.supported_gpus[0].memory}
              ramSize={formatMemoryForDisplay(instance.memory)}
              cpuCount={instance.vcpu}
              cloudProvider={displayPlatformType(
                GetProviderName(instance) || "unknown",
              )}
              cloud={GetCloudName(instance)}
              price={withServiceCharge(
                getFilteredBasePriceAmount(instance, pickerAllowedLocations),
              ).toFixed(2)}
              isFreeTier={
                isEntitled &&
                isFreeTierInstance(
                  getFilteredBasePriceAmount(instance, pickerAllowedLocations),
                )
              }
              flexiblePortControlOnly={flexiblePortControlOnly}
              flexibleStorageOnly={flexibleStorageOnly}
            />
          ))}
      </div>
    </>
  );
};

interface DgxComputeOptionsProps {
  setSelectedInstance(instanceObj: GPUInstanceType | undefined): void;
}

const DGXSetup: React.FC<DgxComputeOptionsProps> = ({
  setSelectedInstance,
}) => {
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const [showNext, setShowNext] = useState(false);
  const dgxFilteredInstances =
    instanceTypesContext.GPUInstancesAvailable.filter((filteredInstances) => {
      return (
        getPrimaryInstanceCloudCred(filteredInstances)?.id === "dgxc-gcp-1"
      );
    });
  const [text] = useWindupString("No DGX Instances Available");

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNext(true);
    }, 2000);
    // Cleanup function
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col bg-black h-[380px] p-4 rounded-md relative">
      <div className="flex flex-row">
        <div className="flex flex-col justify-center items-center w-fit">
          <div className="flex flex-col w-full items-center">
            <div
              className="flex flex-row items-center"
              style={{
                color: "hsl(46.67deg 90% 68.63%)",
              }}
            >
              <svg
                width="24"
                height="16"
                viewBox="0 0 249 165"
                className="mr-1"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M25.43 70.902C25.43 70.902 47.934 37.699 92.867 34.264V22.218C43.098 26.215 0 68.367 0 68.367C0 68.367 24.41 138.932 92.867 145.393V132.589C42.63 126.269 25.43 70.902 25.43 70.902ZM92.867 107.125V118.851C54.899 112.082 44.36 72.614 44.36 72.614C44.36 72.614 62.59 52.419 92.867 49.144V62.011C92.844 62.011 92.828 62.004 92.809 62.004C76.918 60.097 64.504 74.942 64.504 74.942C64.504 74.942 71.462 99.933 92.867 107.125ZM92.867 0V22.218C94.328 22.106 95.789 22.011 97.258 21.961C153.84 20.054 190.707 68.367 190.707 68.367C190.707 68.367 148.364 119.855 104.25 119.855C100.207 119.855 96.422 119.48 92.867 118.85V132.589C95.907 132.975 99.059 133.202 102.348 133.202C143.399 133.202 173.086 112.237 201.832 87.424C206.598 91.241 226.11 100.527 230.121 104.592C202.789 127.475 139.09 145.921 102.977 145.921C99.496 145.921 96.153 145.71 92.867 145.393V164.699H248.899V0H92.867ZM92.867 49.144V34.264C94.313 34.163 95.77 34.086 97.258 34.039C137.946 32.761 164.64 69.004 164.64 69.004C164.64 69.004 135.808 109.047 104.894 109.047C100.445 109.047 96.457 108.332 92.867 107.125V62.011C108.707 63.925 111.894 70.922 121.417 86.797L142.597 68.938C142.597 68.938 127.136 48.661 101.073 48.661C98.24 48.66 95.529 48.859 92.867 49.144Z" />
              </svg>
              <span className="text-2xl font-bold">NVIDIA DGX Cloud</span>
            </div>
          </div>
          <div className="mt-5">
            <img
              src={NvidiaDGXDataCenter}
              alt="NVIDIA NIM Influencer Banner"
              className="h-[300px] w-[500px]"
            />
          </div>
        </div>
        <div className="flex flex-col w-full space-y-6 ml-5 justify-center">
          {dgxFilteredInstances.length === 0 && (
            <div className="flex relative">
              <div className="flex flex-col items-center absolute top-0 w-full">
                <span className="text-xl font-bold text-slate-400">{text}</span>
              </div>
              <DGXComputeCard
                onClick={() => {}}
                disabled={true}
                instance={dummyDGXInstanceObject}
              />
            </div>
          )}
          {dgxFilteredInstances.map((instance) => (
            <>
              <DGXComputeCard
                onClick={() => {
                  setSelectedInstance(instance);
                }}
                disabled={false}
                instance={instance}
              />
              {showNext && (
                <div className="absolute bottom-8 right-8 pulse-ready-button">
                  <Button
                    label={"Next"}
                    style={{
                      backgroundColor: "hsl(46.67deg 90% 68.63%)",
                      color: "#000",
                    }}
                    onClick={() => {
                      setSelectedInstance(instance);
                    }}
                  />
                </div>
              )}
            </>
          ))}
        </div>
      </div>
    </div>
  );
};

interface EmptyStateProps {
  selectedGpuCount: string;
  selectedVram: string;
  onResetFilters: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  selectedGpuCount,
  selectedVram,
  onResetFilters,
}) => {
  const hasActiveFilters = selectedGpuCount !== "All" || selectedVram !== "All";

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="flex flex-col items-center max-w-md text-center">
        {/* Icon */}
        <div className="mb-4 p-4 rounded-full bg-zinc-800/50">
          <FunnelIcon className="w-12 h-12 text-secondary" />
        </div>

        {/* Heading */}
        <h3 className="text-lg font-semibold text-white mb-2">
          No instances found
        </h3>

        {/* Message */}
        <p className="text-sm text-gray-400 mb-6">
          {hasActiveFilters ? (
            <>
              No GPU instances match your current filter criteria. Try adjusting
              or removing some filters to see more options.
            </>
          ) : (
            <>
              No GPU instances match your current filter criteria. Try adjusting
              or removing some filters to see more options.
            </>
          )}
        </p>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="mb-6 p-3 rounded-md bg-zinc-800/50 border border-zinc-700 w-full">
            <p className="text-xs font-medium text-gray-300 mb-2">
              Active filters:
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {selectedGpuCount !== "All" && (
                <span className="inline-flex items-center px-2 py-1 rounded-md bg-highlight/20 text-highlight text-xs">
                  GPU Count: {selectedGpuCount}x
                </span>
              )}
              {selectedVram !== "All" && (
                <span className="inline-flex items-center px-2 py-1 rounded-md bg-highlight/20 text-highlight text-xs">
                  VRAM: {selectedVram}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Reset Button */}
        {hasActiveFilters && (
          <button
            onClick={onResetFilters}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-highlight hover:bg-highlightDarker text-black font-medium text-sm transition-colors duration-200"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Reset Filters
          </button>
        )}
      </div>
    </div>
  );
};

interface CPUOptionsProps {
  cpuInstances: GPUInstanceType[];
  flexiblePortControlOnly: boolean;
  flexibleStorageOnly: boolean;
  pickerAllowedLocations?: Set<string> | null;
  setSelectedInstance(instanceObj: GPUInstanceType | undefined): void;
}

const CPUOptions: React.FC<CPUOptionsProps> = ({
  cpuInstances,
  flexiblePortControlOnly,
  flexibleStorageOnly,
  pickerAllowedLocations,
  setSelectedInstance,
}) => {
  const { isEntitled } = useFreeTier();
  const sortedInstances = useMemo(() => {
    return [...cpuInstances].sort(
      (a, b) =>
        Number(getFilteredBasePriceAmount(a, pickerAllowedLocations) || 0) -
        Number(getFilteredBasePriceAmount(b, pickerAllowedLocations) || 0),
    );
  }, [cpuInstances, pickerAllowedLocations]);

  return (
    <>
      <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white my-3">
        Select a <span className="text-highlight">CPU</span> instance type
      </h2>
      <div className="flex flex-col gap-3">
        {sortedInstances.map((instance) => (
          <CpuInstanceCard
            key={instance.type}
            instance={instance}
            selected={false}
            onSelect={() => setSelectedInstance(instance)}
            isFreeTier={
              isEntitled &&
              isFreeTierInstance(
                getFilteredBasePriceAmount(instance, pickerAllowedLocations),
              )
            }
            flexiblePortControlOnly={flexiblePortControlOnly}
            flexibleStorageOnly={flexibleStorageOnly}
          />
        ))}
      </div>
    </>
  );
};
